import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { EditorComponentBase } from '../../classes/editor-component-base';
import { IGenericTypeAheadDropdownConfig } from '../../interfaces/type-ahead-dropdown-config.interface';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { IconSanitizerService } from '../../services/icon-sanitizer.service';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import { OptionsMenuComponent } from '../options-menu/options-menu.component';
import { SearchInputComponent } from '../search-input/search-input.component';

@Component({
    selector: 'lib-generic-dropdown',
    templateUrl: './generic-dropdown.component.html',
    styleUrls: ['./generic-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, SearchInputComponent, OptionsMenuComponent]
})
export class GenericDropdownComponent<T> extends EditorComponentBase<T> implements OnInit, OnChanges {
    @ViewChild('dropdownButton', { static: true }) dropdownButton!: ElementRef<HTMLButtonElement>;

    @ViewChild('searchInput')
    searchInput?: SearchInputComponent;

    @Input() source?: T[] = [];

    @Input() config?: IGenericTypeAheadDropdownConfig<T>;

    @Input() disabled = false;

    @Input() selectedItem?: T;

    @Output() selectedItemChange = new EventEmitter<T>();

    @Input() override isReadOnly?: boolean;

    @Input() noResultsText? = 'No matches found';

    @Input() placeholder?: string;

    @Input() error? = false;

    @Input() dropdownButtonId?: string;

    @Input() text = '';

    menuOpen = false;

    overlayWidth = signal(0);

    displayText = signal('');

    focusList = signal(false);

    sourceItems?: T[] = [];

    protected scrollStrategy!: ScrollStrategy;

    private readonly overlay = inject(Overlay);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly arrowDownIcon: SafeHtml = this.iconSanitizer.getIcon('arrowDown');
    readonly arrowUpIcon: SafeHtml = this.iconSanitizer.getIcon('arrowUp');

    ngOnInit(): void {
        this.scrollStrategy = this.overlay.scrollStrategies.close();
        this.config = {
            ...this.config,
            clearSelection: this.config?.clearSelection ?? true
        } as IGenericTypeAheadDropdownConfig<T>;
        this.updateDisplayText();
        this.sourceItems = this.source ?? [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedItem']) {
            this.selectedItem = changes['selectedItem']?.currentValue;
            this.updateDisplayText();
        }

        if (changes['source']?.currentValue && !changes['source'].firstChange) {
            this.source = changes['source'].currentValue;
            this.sourceItems = changes['source'].currentValue;
        }
    }

    selectItem(item: T): void {
        this.selectedItem = item;
        this.emitItemChanged(item);
        this.updateDisplayText();
        this.closePanel();
        this.dropdownButton.nativeElement.focus();
    }

    onMenuOpenChange(): void {
        if (!this.menuOpen) this.closePanel();
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.source && this.source.length > 0) {
            if (event.key === 'Enter') {
                this.toggleDropdown();
            } else if (this.menuOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                event.preventDefault();
                this.focusList.set(true);
            } else if (event.key === 'Escape') {
                this.closePanel();
            } else if (event.key === 'Tab') {
                if (this.menuOpen) this.closePanel();
            }
        }
    }

    emitItemChanged(item?: T): void {
        this.selectedItemChange.emit(item);
    }

    toggleDropdown(): void {
        if (this.isReadOnly) return;
        this.sourceItems = this.source;
        this.overlayWidth.set(this.dropdownButton.nativeElement.offsetWidth);
        this.menuOpen = !this.menuOpen;
        this.focusInput();
    }

    closePanel() {
        this.menuOpen = false;
        this.text = '';
        this.focusList.set(false);
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.overlayWidth.set(this.dropdownButton.nativeElement.offsetWidth);
    }

    async onTextChanged(event: string): Promise<void> {
        if (event === '') {
            this.sourceItems = this.source;
        }

        this.sourceItems = this.source?.filter((item) =>
            (item[this.config?.itemDisplayKey as keyof T] as string).toLowerCase().includes(event.toLowerCase())
        );
    }

    focusInput() {
        if (this.menuOpen) {
            setTimeout(() => {
                this.searchInput?.inputElement?.nativeElement.focus();
            }, 0);
        }
    }

    private updateDisplayText(): void {
        if (this.selectedItem && this.config && this.selectedItem[this.config.itemDisplayKey] !== '') {
            this.displayText.set(this.selectedItem[this.config.itemDisplayKey] as unknown as string);
        } else if (!this.selectedItem && this.config && !this.isReadOnly) {
            this.displayText.set(this.config?.defaultText ?? 'Select an item');
        } else if (this.isReadOnly) {
            this.displayText.set('');
        }
    }
}
